/**
 * Custom Deferred Loading
 *
 * Lazy image loading with blur-up placeholder effect.
 * Uses IntersectionObserver to load images when they enter the viewport.
 *
 * Usage:
 *   <custom-deferred-media>
 *     <img
 *       src="{{ image | image_url: width: 50 }}"
 *       data-src="{{ image | image_url: width: 800 }}"
 *       data-srcset="{{ image | image_url: width: 400 }} 400w, {{ image | image_url: width: 800 }} 800w"
 *       alt="..."
 *       class="custom-deferred-media__image"
 *     >
 *   </custom-deferred-media>
 */

(function () {
  'use strict';

  class CustomDeferredMedia extends HTMLElement {
    connectedCallback() {
      this.img = this.querySelector('img[data-src]');
      if (!this.img) return;

      // Apply blur placeholder style
      this.img.style.filter = 'blur(10px)';
      this.img.style.transition = 'filter 0.4s ease';

      this._observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              this._load();
              this._observer.unobserve(entry.target);
            }
          }.bind(this));
        }.bind(this),
        {
          rootMargin: '200px 0px',
          threshold: 0,
        }
      );

      this._observer.observe(this);
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
      }
    }

    _load() {
      var img = this.img;
      var fullSrc = img.dataset.src;
      var fullSrcset = img.dataset.srcset;

      // Create a temporary image to preload
      var tempImg = new Image();

      tempImg.onload = function () {
        if (fullSrcset) {
          img.srcset = fullSrcset;
        }
        img.src = fullSrc;

        // Remove blur after image loads
        requestAnimationFrame(function () {
          img.style.filter = '';
        });

        // Clean up data attributes
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
      };

      tempImg.onerror = function () {
        // Still try to load on error
        img.src = fullSrc;
        img.style.filter = '';
      };

      tempImg.src = fullSrc;
    }
  }

  if (!customElements.get('custom-deferred-media')) {
    customElements.define('custom-deferred-media', CustomDeferredMedia);
  }
})();
